import { useQuery } from '@tanstack/react-query'
import { CalendarDays, Printer, UserRound } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router'
import { errorMessage } from '@/api/errors'
import { EmptyState, ErrorState, LoadingState } from '@/components/feedback/States'
import { FormField } from '@/components/forms/FormField'
import { Button } from '@/components/ui/Button'
import { Card, CardHeader } from '@/components/ui/Card'
import { PageHeader } from '@/components/ui/PageHeader'
import { Select } from '@/components/ui/Select'
import { staffOptionsQuery } from '@/features/academic/api'
import { teacherTimetableQuery } from '@/features/timetable/api'
import { WeekGrid } from '@/features/timetable/components/WeekGrid'
import { WeekSummary } from '@/features/timetable/components/WeekSummary'
import { useDocumentTitle } from '@/hooks/useDocumentTitle'

function TeacherWeekView({ staffId }: { staffId: number }) {
  const { t } = useTranslation('timetable')
  const { data, isPending, error, refetch } = useQuery(teacherTimetableQuery(staffId))
  if (isPending) return <LoadingState />
  if (error) return <ErrorState description={errorMessage(error)} onRetry={() => void refetch()} />
  const subject = data.teacher.primarySubject
  return (
    <div className="space-y-6">
      <WeekSummary week={data} />
      <Card data-print-document>
        <CardHeader
          title={data.teacher.name}
          description={
            subject
              ? t('{{year}} · {{subject}} · {{count}} periods a week', {
                  year: data.academicYear.name,
                  subject: subject.name,
                  count: data.totalPeriods,
                })
              : t('{{year}} · {{count}} periods a week', {
                  year: data.academicYear.name,
                  count: data.totalPeriods,
                })
          }
          icon={<UserRound />}
          actions={
            <Button
              size="sm"
              variant="secondary"
              leftIcon={<Printer />}
              onClick={() => window.print()}
              className="print:hidden"
            >
              {t('Print')}
            </Button>
          }
        />
        <div className="p-3 sm:p-4">
          {data.entries.length === 0 ? (
            <EmptyState
              compact
              icon={<CalendarDays />}
              title={t('No lessons this week')}
              description={t('Assign this teacher to lessons from the class timetables.')}
            />
          ) : (
            <WeekGrid
              week={data}
              view="teacher"
              caption={t('Weekly timetable of {{name}}', { name: data.teacher.name })}
            />
          )}
        </div>
      </Card>
    </div>
  )
}

export function TeacherTimetablesPage() {
  const { t } = useTranslation('timetable')
  useDocumentTitle('Teacher timetables')
  const staff = useQuery(staffOptionsQuery())
  const [params, setParams] = useSearchParams()
  const staffId = Number(params.get('teacher')) || undefined
  const selected = staff.data?.find((s) => s.id === staffId)

  return (
    <>
      <PageHeader
        title={t('Teacher timetables')}
        description={t('Each teacher’s week across all sections, ready to print.')}
        breadcrumbs={[{ label: t('Timetable'), to: '/admin/timetable' }, { label: t('Teacher timetables') }]}
        homeHref="/admin"
      />
      {staff.isPending ? (
        <LoadingState />
      ) : staff.error ? (
        <ErrorState onRetry={() => void staff.refetch()} />
      ) : (
        <>
          <Card className="mb-6 p-4 print:hidden">
            <FormField label={t('Teacher')} className="sm:max-w-sm">
              <Select
                options={staff.data.map((s) => ({ value: s.id, label: s.name }))}
                placeholder={t('Choose a teacher')}
                value={selected?.id ?? ''}
                onChange={(e) =>
                  setParams(e.target.value ? { teacher: e.target.value } : {}, { replace: true })
                }
              />
            </FormField>
          </Card>
          {selected ? (
            <TeacherWeekView key={selected.id} staffId={selected.id} />
          ) : (
            <Card>
              <EmptyState
                icon={<UserRound />}
                title={t('Choose a teacher')}
                description={t('Their lessons, sections and rooms for the week appear here.')}
              />
            </Card>
          )}
        </>
      )}
    </>
  )
}
